import { useEffect, useRef, useState } from "react";
import { Button, Card, CheckboxControl, Label, SectionHeader, SegmentedControl } from "@osago/ui";
import type { SegmentedOption } from "@osago/ui";
import { drawQr, qrDataUrl } from "@/shared/lib/qr";
import { getPrintProduct } from "@/shared/lib/printLayout";
import { openPrintWindow } from "../print/openPrintWindow";
import { ymEvent, YM } from "@/shared/lib/metrika";
import logoUrl from "@/assets/logo.jpg";
import { useReferral } from "../model/referralContext";
import { useReferralLink } from "../model/useReferralLink";
import { LinkPreview } from "./LinkPreview";
import { PosterPreview } from "../print/PosterPreview";

type OutputTab = "link" | "qr" | "print";

const TABS: SegmentedOption<OutputTab>[] = [
  { value: "link", label: "Ссылка" },
  { value: "qr", label: "QR-код" },
  { value: "print", label: "PDF для печати" },
];

const PREVIEW_QR_SIZE = 660;
const EXPORT_QR_SIZE = 1200;

export function LinkOutput() {
  const { partnerId, entryPoint } = useReferral();
  const { parts, url, isReady } = useReferralLink();
  const [tab, setTab] = useState<OutputTab>("link");
  const [branded, setBranded] = useState(true);
  const [copied, setCopied] = useState(false);
  const [printError, setPrintError] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logoRef = useRef<HTMLImageElement | null>(null);
  const [logoReady, setLogoReady] = useState(false);

  useEffect(() => {
    const logo = new Image();
    logo.src = logoUrl;
    logoRef.current = logo;

    if (logo.complete) {
      setLogoReady(true);
      return;
    }

    const onLoad = () => setLogoReady(true);
    logo.addEventListener("load", onLoad);
    return () => logo.removeEventListener("load", onLoad);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isReady || tab !== "qr") return;
    drawQr(canvas, url, PREVIEW_QR_SIZE, { branded, logo: logoRef.current });
  }, [url, branded, isReady, tab, logoReady]);

  if (!isReady) return null;

  async function copy() {
    await navigator.clipboard.writeText(url);
    ymEvent(YM.REFERRAL_COPY);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  function downloadQr() {
    const link = document.createElement("a");
    link.href = qrDataUrl(url, EXPORT_QR_SIZE, { branded, logo: logoRef.current });
    link.download = `osagotop-qr-${partnerId}.png`;
    link.click();
    ymEvent(YM.REFERRAL_QR_DOWNLOAD);
  }

  function print() {
    const opened = openPrintWindow(
      url,
      qrDataUrl(url, EXPORT_QR_SIZE, { branded: true, logo: logoRef.current }),
      logoUrl,
      getPrintProduct(entryPoint),
    );
    setPrintError(opened ? "" : "Браузер заблокировал новое окно — разрешите всплывающие окна");
    if (opened) ymEvent(YM.REFERRAL_PRINT);
  }

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <SectionHeader title="Получение ссылки" />
        <SegmentedControl options={TABS} value={tab} onChange={setTab} />
      </div>

      {tab === "link" && (
        <div className="flex flex-col gap-3">
          <LinkPreview parts={parts} />
          <Label variant="hint" className="block">
            Ссылку можно скопировать кнопкой или выделить прямо в поле. Часть после # — параметры
            партнёрской атрибуции.
          </Label>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => window.open(url, "_blank", "noopener")}
            >
              Открыть
            </Button>
            <Button onClick={copy}>{copied ? "Скопировано" : "Скопировать"}</Button>
          </div>
        </div>
      )}

      {tab === "qr" && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start gap-4">
            <canvas
              ref={canvasRef}
              className="h-44 w-44 rounded-input border border-input-border bg-white"
            />
            <div className="flex min-w-50 flex-1 flex-col gap-2">
              <CheckboxControl
                id="referral-qr-branded"
                checked={branded}
                onChange={setBranded}
                label="Брендированный QR"
              />
              <Label variant="hint" className="block">
                Логотип в центре и фирменные угловые метки. Содержимое ссылки не меняется.
                PNG подойдёт для соцсетей, презентаций и печатных макетов.
              </Label>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={downloadQr}>Скачать PNG</Button>
          </div>
        </div>
      )}

      {tab === "print" && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start gap-5">
            <PosterPreview
              url={url}
              qrSrc={qrDataUrl(url, EXPORT_QR_SIZE, { branded: true, logo: logoRef.current })}
              logoSrc={logoUrl}
              product={getPrintProduct(entryPoint)}
              width={178}
            />
            <div className="flex min-w-55 flex-1 flex-col gap-2">
              <Label variant="hint" className="block">
                Готовый макет A4 для офлайн-размещения: слоган, брендированный QR-код с вашей
                ссылкой и обязательный рекламный дисклеймер. Содержимое подстраивается под
                выбранную страницу входа — ОСАГО, ПКаско или ипотечное страхование.
              </Label>
              <Label variant="hint" className="block">
                Нажмите «Распечатать» — в диалоге печати макет можно сохранить в PDF.
              </Label>
            </div>
          </div>
          {printError && (
            <Label variant="error" className="block">
              {printError}
            </Label>
          )}
          <div className="flex justify-end">
            <Button onClick={print}>Распечатать / сохранить в PDF</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
